/**
 * Reusable functions for handling PDF printing and downloading
 */

/**
 * Extracts filename from Content-Disposition header
 * @param contentDisposition - The Content-Disposition header value
 * @param defaultFileName - Default filename if extraction fails
 * @returns The extracted or default filename
 */
const extractFileName = (
  contentDisposition: string | undefined,
  defaultFileName: string = "file"
): string => {
  if (!contentDisposition) {
    return defaultFileName;
  }

  // Try to extract from filename="..." format
  const matches = contentDisposition.match(/filename="([^"]*)"/);
  if (matches && matches[1]) {
    return matches[1];
  }

  // Try to extract from filename=... format (without quotes)
  const parts = contentDisposition.split(";");
  if (parts.length > 1) {
    const fileNamePart = parts[1].split("=")[1];
    if (fileNamePart) {
      return fileNamePart.trim().replace(/^["']|["']$/g, "");
    }
  }

  return defaultFileName;
};

/**
 * Handles printing a PDF from an API response
 * @param response - The API response containing PDF data
 * @param errorMessage - Optional custom error message
 * @param onError - Optional error callback function
 */
export const handlePrint = (
  response: any,
  errorMessage: string = "No se pudo generar el PDF",
  onError?: (error: any) => void
): void => {
  try {
    const blob = new Blob([response.data], {
      type: response.headers["content-type"] || "application/pdf",
    });
    const url = window.URL.createObjectURL(blob);

    // Open PDF in new tab for printing
    window.open(url, "_blank");

    // Clean up the URL object after a delay
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
  } catch (error) {
    if (onError) {
      onError(error);
    } else {
      throw error;
    }
  }
};

/**
 * Handles downloading a file from an API response
 * @param response - The API response containing file data
 * @param defaultFileName - Default filename if not found in headers
 * @param errorMessage - Optional custom error message
 * @param onError - Optional error callback function
 */
export const handleDownload = (
  response: any,
  defaultFileName: string = "file",
  errorMessage: string = "No se pudo descargar el archivo",
  onError?: (error: any) => void
): void => {
  try {
    const contentDisposition = response.headers["content-disposition"];
    const fileName = extractFileName(contentDisposition, defaultFileName);

    const blob = new Blob([response.data], {
      type: response.headers["content-type"] || "application/pdf",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);

    document.body.appendChild(link);
    link.click();
    link.remove();

    // Clean up the URL object
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 100);
  } catch (error) {
    if (onError) {
      onError(error);
    } else {
      throw error;
    }
  }
};

